import React, { useEffect, useState, useMemo } from 'react';
import Container from '../../components/Container';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from 'react-redux';

import DataFetching from "../../helper/DataFetching";
import renderDataTable from "../../helper/renderDataTable";
import Loading from "../../components/Loading";
import {
    SearchClear,
    formatMoment,
    onBlurDate,
    onFocusDate,
    submitDATE,
    tableDATE
} from '../../helper/CommonFunction';

import Breadcrumb from '../../components/Breadcrumb';

/* ================= NUMBER HELPERS ================= */
const toNumber = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};
const format2 = (v) => toNumber(v).toFixed(2);
const formatInt = (v) => Math.floor(toNumber(v));

const Revenue = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const token = useSelector((state) => state.user.userToken);
    const { domainId, userId } = useParams();

    /* ================= STATES ================= */
    const [filteredData, setFilteredData] = useState([]);
    const [pending, setPending] = useState(true);
    const [resetSearch, setResetSearch] = useState(true);
    const [pageOpen, setPageOpen] = useState(false);

    const [form_date, setForm_date] = useState(null);
    const [end_date, setEnd_date] = useState(null);
    const [form_date1, setForm_date1] = useState("");
    const [end_date1, setEnd_date1] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const [hide, setHide] = useState('');
    const [from, setFrom] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");

    /* ================= FETCH DATA ================= */
    const fetchData = async () => {
        setPending(true);

        const body = {
            page: currentPage - 1,
            perpage: pageLimit,
            user_id: userId,
            domain_id: domainId,
            start_date: form_date1,
            end_date: end_date1,
            is_active: statusFilter,
        };

        DataFetching(
            setPending,
            '/get_revenue_data.php',
            'POST',
            token,
            dispatch,
            navigate,
            setFilteredData,
            setTotalPages,
            body,
            setResetSearch,
            resetSearch
        );
    };

    /* ================= SEARCH ================= */
    const handleSearchClick = () => {
        if (form_date && !end_date) {
            setHide("This field is required");
            return;
        }
        if (hide || from) return;

        setCurrentPage(1);
        setResetSearch(prev => !prev);
    };

    const handleClearSearch = () => {
        setHide('');
        setFrom(false);
        clearSearchData();
        setResetSearch(prev => !prev);
    };

    const clearSearchData = () => {
        setForm_date(null);
        setForm_date1("");
        setEnd_date(null);
        setEnd_date1("");
        setCurrentPage(1);
        setPageLimit(10);
        setStatusFilter("");
    };

    /* ================= EFFECT ================= */
    useEffect(() => {
        if (!resetSearch) return;
        fetchData();
    }, [currentPage, pageLimit, location, resetSearch, userId, domainId]);

    /* ================= ACTION ================= */
    const handleAddRevenue = () => {
        navigate(`/admin/users/domains/${domainId}/${userId}/revenue/add`);
    };

    /* ================= TOTAL CALCULATION ================= */
    const totals = useMemo(() => {
        const rowCount = filteredData.length || 1; // total days/rows

        const sum = filteredData.reduce(
            (acc, row) => {
                acc.revenue += toNumber(row.revenue);
                acc.cpm += toNumber(row.cpm);
                acc.fill_rate += toNumber(row.fill_rate);
                acc.viewability += toNumber(row.viewability);
                acc.impressions += toNumber(row.impressions);
                acc.click += toNumber(row.click);
                acc.ctr += toNumber(row.ctr);
                return acc;
            },
            {
                revenue: 0,
                cpm: 0,
                fill_rate: 0,
                viewability: 0,
                impressions: 0,
                click: 0,
                ctr: 0,
            }
        );

        return {
            revenue: sum.revenue,
            cpm: sum.cpm,

            // ✅ AVERAGE FIELDS
            fill_rate: sum.fill_rate / rowCount,     // Match Rate
            viewability: sum.viewability / rowCount, // Show Rate
            ctr: sum.ctr / rowCount,                 // CTR

            impressions: sum.impressions,
            click: sum.click,
        };
    }, [filteredData]);

    /* ================= TABLE DATA (WITH TOTAL ROW) ================= */
    const tableData = useMemo(() => {
        if (!filteredData.length) return [];

        return [
            ...filteredData,
            {
                isTotal: true,
                created_date: "Total",
                app_name: "",
                revenue: format2(totals.revenue),
                cpm: format2(totals.cpm),
                fill_rate: format2(totals.fill_rate),
                viewability: format2(totals.viewability),
                impressions: formatInt(totals.impressions),
                click: formatInt(totals.click),
                ctr: format2(totals.ctr),
                is_active: ""
            }
        ];
    }, [filteredData, totals]);

    /* ================= TABLE COLUMNS ================= */
    const column = [
        {
            name: "Date",
            selector: row =>
                row.isTotal ? <strong>Total</strong> : formatMoment(row.created_date),
        },
        { name: "App Name", selector: row => row.app_name },
        { name: "Revenue($)", selector: row => row.revenue },
        { name: "CPM", selector: row => row.cpm },
        { name: "Match Rate", selector: row => row.fill_rate },
        { name: "Show Rate", selector: row => row.viewability },
        { name: "Impressions", selector: row => row.impressions },
        { name: "Click", selector: row => row.click },
        { name: "CTR", selector: row => row.ctr },
        {
            name: "Status",
            selector: row => {
                if (row.isTotal) return "";

                const isActive = String(row.is_active) === "1";

                return (
                    <span
                        className={`px-2 py-1 text-xs rounded font-medium ${isActive
                            ? "bg-orange-100 text-orange-700"
                            : "bg-red-100 text-red-700"
                            }`}
                    >
                        {isActive ? "Active" : "Inactive"}
                    </span>
                );
            },
        },

    ];

    if (pageOpen) return <Loading />;

    return (
        <>
            <Breadcrumb pageName="User Revenue Table" />
            <Container>
                <div className="border-2 border-[#ddddddbe] w-full p-3">

                    {/* ADD BUTTON */}
                    <div className="text-right mb-2">
                        <button
                            onClick={handleAddRevenue}
                            className="bg-lime-500 text-white text-[13px] px-2 py-1 rounded"
                        >
                            Add Revenue
                        </button>
                    </div>

                    {/* FILTERS */}
                    <div className="bg-[#f7f7f7] p-3 mb-3">
                        <p className="text-lg font-medium mb-2">Filters:</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                            <DatePicker
                                selected={form_date}
                                placeholderText="From Signup Date"
                                className={`form-control ${from ? "is-invalid" : ""}`}
                                onChange={(date) =>
                                    tableDATE(date, setForm_date, setForm_date1, end_date, setFrom)
                                }
                                dateFormat="yyyy-MM-dd"
                                onFocus={() => onFocusDate(setHide)}
                                onBlur={() => onBlurDate(setHide, form_date, end_date)}
                            />

                            <DatePicker
                                selected={end_date}
                                placeholderText="To Signup Date"
                                className={`form-control ${hide ? "is-invalid" : ""}`}
                                onChange={(date) =>
                                    submitDATE(date, setHide, setEnd_date, setEnd_date1, setFrom, form_date)
                                }
                                dateFormat="yyyy-MM-dd"
                                minDate={form_date}
                            />

                            <select
                                className="form-control"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <SearchClear search={handleSearchClick} clear={handleClearSearch} />
                    </div>

                    {/* TABLE */}
                    {renderDataTable(
                        tableData,          // ✅ TOTAL INCLUDED
                        column,
                        pending,
                        currentPage,
                        setPageLimit,
                        setCurrentPage,
                        totalPages,
                        pageLimit,
                        setResetSearch,
                        dispatch,
                        navigate
                    )}
                </div>
            </Container>
        </>
    );
};

export default Revenue;
